import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro';
import { BALANCE_COLUMN_FIELDS } from '../RosterPlan.consts';
import {
  StyledPanelBox,
  StyledRosterPlanDataGridPro
} from '../RosterPlan.styles';
import CustomColumnMenu from './CustomColumnMenu';
import NoDataGridRows from './NoDataGridRows';
import { getEmployeeRowHeight } from '../utils/getEmployeeRowHeight';
import type { RosterEmployeesGridProps } from './RosterEmployeesGrid.types';

const COLUMNS_WITH_NO_DASHED_BORDER = ['sortorder', ...BALANCE_COLUMN_FIELDS];

const RosterEmployeesGrid = ({
  rows,
  columns,
  apiRef,
  isLoading,
  getRowClassName,
  columnGroupingModel,
  employeeNameFormat,
  onSelectEmployeeNameFormat
}: RosterEmployeesGridProps) => {
  'use memo';

  return (
    <StyledPanelBox>
      <StyledRosterPlanDataGridPro
        rows={rows}
        columns={columns}
        className="roster-grid"
        apiRef={apiRef}
        hideFooter
        /**
         * The four fields the backend itself treats as the row's identity. Rows are uniqued on
         * `(workAgreementId, plannedCostCenterId)` before the benefit loop emits one row per
         * benefit (`RosterEmployeesBuilderGroovyBase.groovy`), so an employee can appear once per
         * employment, once per plan unit they are deployed in, and once per benefit inside that.
         *
         * `workAgreementId` cannot be dropped as implied by `empBenefitId`: the builder synthesises
         * a benefit-less row (`filteredBenefits = [null]`) and emits `empBenefitId: benefit?.id`,
         * so two work agreements of one employee at one plan unit with no benefit in the period
         * both key on a null benefit and would collide again. Same four fields as the Dojo store
         * (`_BaseEmployeeStore.js:40`).
         *
         * A duplicate id does not throw: DataGridPro pushes it into the row tree twice while the
         * lookup keeps only the last model, so the grid renders two identical rows. A fresh uuid
         * per render used to be the fallback, which was unique but broke selection, memoisation
         * and virtualisation on every render.
         */
        getRowId={(row) =>
          `${row.id}-${row.workAgreementId}-${row.customerId}-${row.empBenefitId}`
        }
        getRowClassName={getRowClassName}
        getRowHeight={(params) => getEmployeeRowHeight(params?.model)}
        initialState={{
          pinnedColumns: {
            left: [
              GRID_CHECKBOX_SELECTION_COL_DEF.field,
              'sortorder',
              'employee'
            ],
            right: [
              GRID_CHECKBOX_SELECTION_COL_DEF.field,
              ...BALANCE_COLUMN_FIELDS
            ]
          },
          sorting: {
            sortModel: [{ field: 'employee', sort: 'asc' }]
          }
        }}
        firstColumnName="employee"
        columnsWithNoDashedBorder={COLUMNS_WITH_NO_DASHED_BORDER}
        columnGroupingModel={columnGroupingModel}
        slotProps={{
          columnHeaders: { className: 'week-header-row' },
          noRowsOverlay: { isLoading },
          columnMenu: { employeeNameFormat, onSelectEmployeeNameFormat }
        }}
        slots={{
          noRowsOverlay: NoDataGridRows,
          columnMenu: CustomColumnMenu
        }}
      />
    </StyledPanelBox>
  );
};

export default RosterEmployeesGrid;
